"use client";

import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Wholesaler } from "@/types/models/wholesaler";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { EllipsisVertical, Store, Package, Phone, Flag } from "lucide-react";
import { Link, useParams } from "react-router-dom";

const getOnboardingStatusColor = (completed: boolean) => {
  return completed
    ? "bg-green-500/20 text-green-600 hover:bg-green-500/20"
    : "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20";
};

export const columns: ColumnDef<Wholesaler>[] = [
  {
    id: "select",
    size: 50,
    minSize: 40,
    maxSize: 60,
    header: ({ table }) => {
      const { rows } = table.getRowModel();
      const selectedRows = table.getSelectedRowModel().rows;
      const isAllSelected = rows.length > 0 && selectedRows.length === rows.length;
      const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

      return (
        <div
          className="flex items-center justify-center transition-opacity duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isAllSelected ? true : isSomeSelected ? "indeterminate" : false}
            onCheckedChange={(value) => {
              if (value) {
                table.toggleAllPageRowsSelected(true);
              } else {
                table.toggleAllPageRowsSelected(false);
              }
            }}
            aria-label="Select all"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
    cell: ({ row }) => {
      const isSelected = row.getIsSelected();

      return (
        <div
          className={`flex items-center justify-center transition-opacity duration-200 ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
  },
  {
    accessorKey: "Name",
    size: 280,
    minSize: 220,
    maxSize: 350,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Wholesaler</span>
          <div className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const wholesaler = row.original;
      const storeName = (wholesaler as any).Name || (wholesaler as any).name || "Unnamed wholesaler";
      const storeDomain = (wholesaler as any).Domain || (wholesaler as any).domain || "";
      const date = wholesaler.updated_at ? new Date(wholesaler.updated_at) : new Date();
      const formattedDate = format(date, "MMM dd yyyy");

      return (
        <div className="flex gap-3 items-center">
          <div className="w-10 h-10 overflow-hidden object-cover rounded-md flex-shrink-0">
            {wholesaler.logo ? (
              <img
                src={getAsset(wholesaler.logo, { width: 60, height: 60, fit: "cover" })}
                alt={storeName}
                className="w-10 h-10 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = getAsset("seller/b66f0c3b-54dc-4564-af3a-848196817901/products/images/bljmuedorxDSsvOtpgQP.jpg", { width: 60, height: 60, fit: "cover" });
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
                <Store className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold truncate max-w-[200px]" title={storeName}>
              {storeName}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">{formattedDate}</span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground font-mono">{storeDomain || "No domain"}</span>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "category",
    size: 150,
    minSize: 120,
    maxSize: 200,
    header: ({ column }) => {
      return (
        <div className="group">
          <Button onClick={() => column.toggleSorting(column.getIsSorted() === "asc")} variant="ghost" className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit">
          <span className="text-base font-bold">Collection</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const category = row.original.category;
      return (
        <div>
          <h3 className="text-xs font-semibold truncate capitalize">{category || "Uncategorized"}</h3>
        </div>
      );
    },
  },
  {
    accessorKey: "about",
    size: 250,
    minSize: 200,
    maxSize: 300,
    header: () => <span className="text-base font-bold">Description</span>,
    cell: ({ row }) => {
      const about = row.original.about;
      if (!about) return (<div><span className="text-xs text-gray-500">No description</span></div>);
      const truncatedAbout = about.length > 100 ? about.substring(0, 100) + "..." : about;
      return (<div className="max-w-[200px]" title={about}><p className="text-xs text-gray-700 line-clamp-3">{truncatedAbout}</p></div>);
    },
  },
  {
    accessorKey: "onboarding_completed",
    size: 130,
    minSize: 100,
    maxSize: 150,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const completed = row.original.onboarding_completed;
      return (
        <Badge variant="secondary" className={`rounded-full px-[10px] py-[5px] ${getOnboardingStatusColor(completed)}`}>
          {completed ? "Completed" : "Pending"}
        </Badge>
      );
    },
  },
  {
    accessorKey: "actions",
    size: 150,
    minSize: 120,
    maxSize: 200,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row }) => <WholesalerActionsCell wholesaler={row.original} />,
  },
];

const WholesalerActionsCell: React.FC<{ wholesaler: Wholesaler }> = ({ wholesaler }) => {
  const params = useParams();
  if (!params) return null;
  const { storeType, storeId } = params as any;

  return (
    <div className="flex items-center gap-2">
      <Link to={`/${storeType}/${storeId}/product/wholesaler/${wholesaler.id}`}>
        <Button variant="default" size="sm" className="h-8 px-3 text-xs bg-primary text-primary-foreground hover:bg-primary/90">View</Button>
      </Link>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><EllipsisVertical className="h-4 w-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="bg-card dark:bg-card text-foreground" align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Package className="h-4 w-4 mr-2" />
            See Products
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Store className="h-4 w-4 mr-2" />
            See Collections
          </DropdownMenuItem>
          <DropdownMenuItem>
            <Phone className="h-4 w-4 mr-2" />
            Get Contact Info
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600 focus:text-red-600">
            <Flag className="h-4 w-4 mr-2" />
            Report Seller
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
